import { 
    Stack, 
    DefaultButton, 
    PrimaryButton,
    TextField,
    Label,
    Text,
    mergeStyles,
    Icon,
    Dialog,
    DialogFooter,
    DialogType,
    Spinner,
    IconButton,
} from "@fluentui/react";
import { ContainerInfo, ContainerListInfo } from "../../../api/admin/models_admin";
import { LinkButton, formatDateTime } from "../components";
import { useEffect, useState } from "react";
import { deleteAiSearch, deleteContainer, getContainer, makeAiSearch, renameContainer, runIndexer, swapContainerOrderDown, swapContainerOrderUp } from "../../../api/admin/api_admin";

const accordionPanelStyle = mergeStyles({
    padding: '16px',
    backgroundColor: '#faf9f8',
    borderRadius: '4px',
    marginBottom: '8px',
    border: '1px solid #e1dfdd'
});

interface UploadAccordionElementProps{
    containerListInfo: ContainerListInfo
    isFirst: boolean
    isLast: boolean
    onError: (error: string) => void
    onSuccess: (message: string) => void
    updateList: () => Promise<void>
    isExpanded: boolean
    setIsExpanded: (value: boolean) => void
}

export const UploadAccordionElement = (props: UploadAccordionElementProps) => {
    const {containerListInfo, isFirst, isLast, onError, onSuccess, updateList, isExpanded, setIsExpanded} = props
    const containerSeq = containerListInfo.seq

    const [containerData, setContainerData] = useState<ContainerInfo | null>(null)
    const [isLoadingContaier, setIsLoadingContainer] = useState(false)
    
    const [deleteAiSearchDialogVisible, setDeleteAiSearchDialogVisible] = useState(false)
    const [deletingAiSearch, setDeletingAiSearch] = useState<boolean>(false);

    const [deleteDialogVisible, setDeleteDialogVisible] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);

    const [isAiSearchConfiguring, setIsAiSearchConfiguring] = useState(false)

    const [newContainerName, setNewContainerName] = useState<null | string>(null)
    const [isRenaming, setIsRenaming] = useState(false)

    const [isStartingIndexer, setIsStartingIndexer] = useState(false)

    const currentCntName = (containerData === null ? containerListInfo.container_name : containerData.container_name)
    const currentRagRegist = (containerData === null ? containerListInfo.rag_regist : containerData.rag_regist)
    const currentCngValues = (containerData === null ? containerListInfo.cng_values : containerData.cng_values)
    const havePermission = containerListInfo.can_access
    const canAccess = containerData !== null && containerListInfo.can_access && !isLoadingContaier
    const indexerLastSucceededStr = formatDateTime(containerData?.indexer_last_succeeded)
    const isIndexerRunning = containerData?.is_indexer_running ?? false
    // AI Searchから反映状態を取得できなかった場合。実行中かどうか判らないため反映させない
    const isIndexerStatusUnknown = containerData !== null && containerData.is_indexer_running === null
    // 未反映の変更があるか判定できない場合(インデクサが一度も実行されていない等)
    const isUnindexedUnknown = containerData !== null && containerData.exists_unindexed_file === null
    const syncNoteText = isIndexerStatusUnknown ? "反映状態を取得できませんでした"
        : isIndexerRunning ? "反映中です（時間がかかることがあります）"
        : !currentRagRegist ? "文書検索設定がされていません"
        : isUnindexedUnknown ? "未反映の変更があるか確認できませんでした"
        : undefined
    // 反映が済んでいない変更がある場合の警告。
    // 実行中のインデクサが拾うのは実行開始時点までの変更のため、反映中でも開始後の変更があれば警告する
    const showUnindexedWarning = containerData?.exists_unindexed_file ?? false
    const unindexedWarningText = isIndexerRunning
        ? "反映開始後に変更されたファイルがあります（次回の反映が必要です）"
        : "最終反映後に変更されたファイルがあります"

    const accordionHeaderStyle = mergeStyles({
        padding: '12px 16px',
        backgroundColor: havePermission ? '#f3f2f1' : '#e8e8e8',
        cursor: havePermission ? 'pointer' : "default",
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderRadius: '4px',
        marginBottom: '8px',
        opacity: havePermission ? 1 : 0.5,
        filter: havePermission ? 'none' : 'grayscale(50%)',
        ':hover': {
            backgroundColor: havePermission ? '#e1dfdd' : '#e8e8e8'
        }
    });

    useEffect(() => {
        if(!isExpanded) return 
        updateContainerData()
    }, [isExpanded])

    const toggleExpandState = () => {
        setIsExpanded(!isExpanded)
    }

    const updateContainerData = async () => {
        try{
            setIsLoadingContainer(true)
            const data = await getContainer(containerSeq)
            if ("error" in data) {
                onError(data.error)
            }
            else {
                setContainerData(data)
                setNewContainerName(data.container_name)
            }
        } finally {
            setIsLoadingContainer(false)
        }
    }

    const handleRenameSubmit = async () => {
        if (newContainerName === null) {
            onError("データが読み込まれていません");
            return;
        }
        if (!newContainerName.trim()) {
            onError("カテゴリ名を入力してください");
            return;
        }

        try {
            setIsRenaming(true)
            const result = await renameContainer({ 
                container_seq: containerSeq, 
                new_container_name: newContainerName 
            });
            if (result.success) {
                onSuccess("カテゴリ名の変更に成功しました");
                await updateContainerData();
            } else {
                onError(result.message || "カテゴリ名の変更に失敗しました");
            }
        } catch (err) {
            onError("カテゴリ名の変更中にエラーが発生しました");
        } finally {
            setIsRenaming(false)
        }
    };

    const handleMoveUp = async () => {
        if (isFirst) return;
        
        try {
            const result = await swapContainerOrderUp(containerSeq);
            if (result.success) {
                await updateList();
            } else {
                onError(result.message || "並び順の変更に失敗しました");
            }
        } catch (err) {
            onError("並び順の変更中にエラーが発生しました");
        }
    };

    const handleMoveDown = async () => {
        if (isLast) return;
        
        try {
            const result = await swapContainerOrderDown(containerSeq);
            if (result.success) {
                await updateList();
            } else {
                onError(result.message || "並び順の変更に失敗しました");
            }
        } catch (err) {
            onError("並び順の変更中にエラーが発生しました");
        }
    };

    const handleMakeAiSearch = async () => {
        setIsAiSearchConfiguring(true)
        try {
            const result = await makeAiSearch(containerSeq);
            if (result.success) {
                onSuccess("文書検索設定を開始しました。設定には時間がかかる場合があります。");
                await updateContainerData();
            } else {
                onError(result.message || "文書検索設定に失敗しました");
            }
        } catch (err) {
            onError("文書検索設定中にエラーが発生しました");
        } finally{
            setIsAiSearchConfiguring(false)
        }
    };
    
    const handleDeleteAiSearchClick = () => {
        setDeleteAiSearchDialogVisible(true)
    };

    const handleRunIndexerManually = async () => {
        setIsStartingIndexer(true)
        try {
            const result = await runIndexer({container_seq: containerSeq})
            if (result.success) {
                onSuccess(result.message);
                await updateContainerData();
            } else {
                onError(result.message);
            }
        } catch (err) {
            onError("検索への反映中にエラーが発生しました。時間をおいて再度実行してください。");
        } finally {
            setIsStartingIndexer(false);
        }
    };
    
    const handleDeleteAiSearchConfirm = async () => {
        setDeletingAiSearch(true);
        try {
            const result = await deleteAiSearch(containerSeq);
            if (result.success) {
                onSuccess("文書検索設定を解除しました");
                setDeleteAiSearchDialogVisible(false);
                await updateContainerData();
            } else {
                onError(result.message || "文書検索設定の解除に失敗しました");
                setDeleteAiSearchDialogVisible(false);
            }
        } catch (err) {
            onError("文書検索設定の解除中にエラーが発生しました");
            setDeleteAiSearchDialogVisible(false);
        } finally {
            setDeletingAiSearch(false);
        }
    };
    
    const handleDeleteAiSearchCancel = () => {
        setDeleteAiSearchDialogVisible(false)
    };

    const handleDeleteClick = () => {
        if (containerData === null || containerData.rag_regist) {
            onError("文書検索設定済のカテゴリは削除できません。文書検索設定を解除してから削除してください。");
            return;
        }
        setDeleteDialogVisible(true);
    };
    
    const handleDeleteConfirm = async () => {
        setDeleting(true);
        try {
            const result = await deleteContainer(containerSeq);
            if (result.success) {
                onSuccess("カテゴリの削除に成功しました");
                setDeleteDialogVisible(false);
                await updateList();
            } else {
                onError(result.message || "削除に失敗しました");
                setDeleteDialogVisible(false);
            }
        } catch (err) {
            onError("削除中にエラーが発生しました");
            setDeleteDialogVisible(false);
        } finally {
            setDeleting(false);
        }
    };
    
    const handleDeleteCancel = () => {
        setDeleteDialogVisible(false);
    };

    return (
        <div key={containerSeq} style={{ marginBottom: '8px' }} data-testid={`upload-category-${containerSeq}`}>
            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                {/* 並び順変更ボタン */}
                <Stack verticalAlign="center" styles={{ root: { width: '50px' } }}>
                    <DefaultButton 
                        text="上へ"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleMoveUp();
                        }}
                        disabled={isFirst || !havePermission}
                        styles={{ 
                            root: { 
                                width: '50px', 
                                height: '22px',
                                minHeight: '22px',
                                minWidth: '50px',
                                padding: '0 4px',
                                fontSize: '11px'
                            }
                        }}
                    />
                    <DefaultButton 
                        text="下へ"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleMoveDown();
                        }}
                        disabled={isLast || !havePermission}
                        styles={{ 
                            root: { 
                                width: '50px', 
                                height: '22px',
                                minHeight: '22px',
                                minWidth: '50px',
                                padding: '0 4px',
                                fontSize: '11px',
                                marginTop: '2px'
                            }
                        }}
                    />
                </Stack>
                
                {/* アコーディオンヘッダー */}
                <div 
                    className={accordionHeaderStyle} 
                    onClick={() => {
                        if(havePermission && !isLoadingContaier){
                            toggleExpandState()
                        }
                    }}
                    style={{ flex: 1 }}
                >
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }} styles={{ root: { width: '100%' } }}>
                        <Stack.Item>
                            <Icon iconName={isExpanded ? "ChevronDown" : "ChevronRight"} />
                        </Stack.Item>
                        {
                            isLoadingContaier && <Spinner />
                        }
                        <Stack.Item>
                            <Label styles={{ root: { fontWeight: 600 } }}>{currentCntName}</Label>
                        </Stack.Item>
                        {currentRagRegist && (
                            <Stack.Item>
                                <span style={{ 
                                    backgroundColor: '#dff6dd', 
                                    color: '#107c10', 
                                    padding: '4px 8px', 
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: 600
                                }}>
                                    ○ 設定済
                                </span>
                            </Stack.Item>
                        )}
                        {currentCngValues && (
                            <Stack.Item>
                                <span style={{ fontSize: '12px', color: '#605e5c' }}>グループ: {currentCngValues}</span>
                            </Stack.Item>
                        )}
                    </Stack>
                </div>
            </Stack>
            
            {isExpanded && 
                (<div className={accordionPanelStyle}>
                    <Stack tokens={{ childrenGap: 12 }}>
                        {/* 表示内容の再読み込み */}
                        <Stack horizontal horizontalAlign="space-between">
                            {/* カテゴリ名変更 */}
                            <Stack horizontal tokens={{ childrenGap: 16 }}>
                                <Label styles={{ root: { width: '150px', fontWeight: 600 } }}>カテゴリ名変更</Label>
                                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end" styles={{ root: { flex: 1 } }}>
                                    <TextField
                                        value={newContainerName ?? currentCntName}
                                        onChange={(_, newValue) => {
                                            setNewContainerName(newValue || "");
                                        }}
                                        disabled={!canAccess}
                                        styles={{ root: { width: 300 } }}
                                    />
                                    <PrimaryButton 
                                        text="変更" 
                                        onClick={handleRenameSubmit}
                                        disabled={!canAccess || newContainerName === containerData?.container_name || isRenaming}
                                    />
                                </Stack>
                            </Stack>
                            <IconButton
                                iconProps={{
                                    iconName: 'Refresh',
                                }}
                                title="表示内容を最新の状態に更新"
                                ariaLabel="表示内容を最新の状態に更新"
                                onClick={updateContainerData}
                                disabled={!havePermission || isLoadingContaier}
                                styles={{
                                    root: {
                                        width: 32,
                                        height: 32,
                                        border: '1px solid #e1dfdd',
                                        backgroundColor: '#ffffff'
                                    },
                                    rootHovered: { backgroundColor: '#f3f2f1' }
                                }}
                            />
                        </Stack>
                        {/* ファイル編集 */}
                        <Stack horizontal tokens={{ childrenGap: 16 }}>
                            <Label styles={{ root: { width: '150px', fontWeight: 600 } }}>ファイル一覧・編集</Label>
                            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center" styles={{ root: { flex: 1 } }}>
                                <PrimaryButton  
                                    onClick={() => window.open(`/menu/upload/list_files/${containerSeq}`)}
                                    text="ファイル一覧・編集（アップロード・削除）画面を開く" 
                                    disabled={!canAccess}
                                />
                            </Stack>
                        </Stack>
                        {/* 検索への反映 */}
                        <Stack horizontal tokens={{ childrenGap: 16 }}>
                            <Label styles={{ root: { width: '150px', fontWeight: 600 } }}>検索への反映</Label>
                            <Stack horizontal wrap tokens={{ childrenGap: 8 }} verticalAlign="center" styles={{ root: { flex: 1 } }}>
                                <Label>{indexerLastSucceededStr}</Label>
                                <DefaultButton
                                    text="今すぐ反映する"
                                    onClick={handleRunIndexerManually}
                                    disabled={!canAccess || isIndexerRunning || isIndexerStatusUnknown || !currentRagRegist || isStartingIndexer}
                                />
                                {showUnindexedWarning && (
                                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }} styles={{ root: {
                                        backgroundColor: '#fff4ce',
                                        border: '1px solid #f2c811',
                                        borderRadius: '4px',
                                        padding: '4px 8px'
                                    } }}>
                                        <Icon iconName="Warning" styles={{ root: { color: '#8a6116', fontSize: 14 } }} />
                                        <Text variant="small" styles={{ root: { color: '#8a6116', fontWeight: 600 } }}>
                                            {unindexedWarningText}
                                        </Text>
                                    </Stack>
                                )}
                                {syncNoteText && (
                                    <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                                        {syncNoteText}
                                    </Text>
                                )}
                            </Stack>
                        </Stack>
                        {/* 文書検索設定 */}
                        <Stack horizontal tokens={{ childrenGap: 16 }}>
                            <Label styles={{ root: { width: '150px', fontWeight: 600 } }}>文書検索設定</Label>
                            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center" styles={{ root: { flex: 1 } }}>
                                {currentRagRegist ? (
                                    <>
                                        <Label>○ 設定済</Label>
                                        <DefaultButton 
                                            text="解除する" 
                                            onClick={handleDeleteAiSearchClick}
                                            disabled={!canAccess || isAiSearchConfiguring || isStartingIndexer}
                                        />
                                    </>
                                ) : (
                                    <>
                                        {isAiSearchConfiguring ? <Spinner /> : <Label>× 未設定</Label>}
                                        <PrimaryButton 
                                            text="設定する" 
                                            onClick={handleMakeAiSearch}
                                            disabled={!canAccess || isAiSearchConfiguring || isStartingIndexer}
                                        />
                                    </>
                                )}
                            </Stack>
                        </Stack>
                        {/* システムプロンプト */}
                        <Stack horizontal tokens={{ childrenGap: 16 }}>
                            <Label styles={{ root: { width: '150px', fontWeight: 600 } }}>システムプロンプト</Label>
                            <Stack styles={{ root: { flex: 1 } }}>
                                <LinkButton 
                                    link_to={`/menu/upload/update_system_prompt/${containerSeq}`} 
                                    text="システムプロンプト変更" 
                                    disabled={!canAccess}
                                />
                            </Stack>
                        </Stack>
                        {/* グループ */}
                        <Stack horizontal tokens={{ childrenGap: 16 }}>
                            <Label styles={{ root: { width: '150px', fontWeight: 600 } }}>グループ</Label>
                            <Stack styles={{ root: { flex: 1 } }}>
                                <LinkButton 
                                    link_to={`/menu/upload/update_group/${containerSeq}`} 
                                    text="グループ変更" 
                                    disabled={!canAccess}
                                />
                            </Stack>
                        </Stack>
                        {/* サンプルプロンプト */}
                        <Stack horizontal tokens={{ childrenGap: 16 }}>
                            <Label styles={{ root: { width: '150px', fontWeight: 600 } }}>サンプルプロンプト</Label>
                            <Stack styles={{ root: { flex: 1 } }}>
                                <LinkButton 
                                    link_to={`/menu/upload/update_user_prompt_example/${containerSeq}`} 
                                    text="プロンプト例変更" 
                                    disabled={!canAccess}
                                />
                            </Stack>
                        </Stack>
                        {/* サブカテゴリ */}
                        <Stack horizontal tokens={{ childrenGap: 16 }}>
                            <Label styles={{ root: { width: '150px', fontWeight: 600 } }}>サブカテゴリ</Label>
                            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center" styles={{ root: { flex: 1 } }}>
                                {/* ファイル一覧・編集と同じく、編集内容を抱えたまま画面遷移されないよう別ウィンドウで開く */}
                                <DefaultButton
                                    onClick={() => window.open(`/menu/upload/update_tags/${containerSeq}`)}
                                    text="サブカテゴリ設定画面を開く"
                                    disabled={!canAccess}
                                    styles={{ root: { width: 'fit-content' } }}
                                />
                            </Stack>
                        </Stack>
                        {/* 高評価/低評価 */}
                        {(
                            <Stack horizontal tokens={{ childrenGap: 16 }}>
                                <Label styles={{ root: { width: '150px', fontWeight: 600 } }}>高評価/低評価</Label>
                                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center" styles={{ root: { flex: 1 } }}>
                                    <Label>
                                        {canAccess && containerData
                                            ? containerData.feedback_text
                                            : '-'}
                                    </Label>
                                    <LinkButton 
                                        link_to={`/api/download_feedback_csv?seq=${containerSeq}`} 
                                        text="詳細を取得" 
                                        disabled={!canAccess || containerData === null || !containerData?.csv_available}
                                        external
                                    />
                                </Stack>
                            </Stack>
                        )}
                        {/* カテゴリ削除 */}
                        <Stack horizontal tokens={{ childrenGap: 16 }}>
                            <Label styles={{ root: { width: '150px', fontWeight: 600 } }}>カテゴリ削除</Label>
                            <Stack >
                                {containerData?.rag_regist ? (
                                    <Label>削除不可（文書検索設定を先に解除してください）</Label>
                                ) : (
                                    <DefaultButton 
                                        text="削除" 
                                        onClick={handleDeleteClick}
                                        disabled={!canAccess || containerData === null}
                                    />
                                )}
                            </Stack>
                        </Stack>
                    </Stack>
                </div>
            )}

            {/* AI Search解除確認ダイアログ */}
            <Dialog
                hidden={!deleteAiSearchDialogVisible}
                onDismiss={handleDeleteAiSearchCancel}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: '文書検索設定解除確認',
                    subText: `カテゴリ「${containerData?.container_name}」の文書検索設定を解除してもよろしいですか?`
                }}
            >
                <DialogFooter>
                    <PrimaryButton onClick={handleDeleteAiSearchConfirm} text="解除" disabled={deletingAiSearch} />
                    <DefaultButton onClick={handleDeleteAiSearchCancel} text="キャンセル" disabled={deletingAiSearch} />
                </DialogFooter>
            </Dialog>

            {/* 削除確認ダイアログ */}
            <Dialog
                hidden={!deleteDialogVisible}
                onDismiss={handleDeleteCancel}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: '削除確認',
                    subText: `カテゴリ「${containerData?.container_name}」を削除してもよろしいですか?`
                }}
            >
                <DialogFooter>
                    <PrimaryButton onClick={handleDeleteConfirm} text="削除" disabled={deleting} />
                    <DefaultButton onClick={handleDeleteCancel} text="キャンセル" disabled={deleting} />
                </DialogFooter>
            </Dialog>
        </div>
    );
};